/**
 * The single source of truth for "what plan is this person on?".
 *
 * Reported defect: `/studio/settings` and `/studio/billing` gave the visitor two
 * different answers to the same question. Settings hardcoded
 * `Current: Creator Plan ($9/mo)` and `Shared outputs: 3/10 · Source storage:
 * 0.5GB/5GB` as JSX literals (settings-page.tsx:45-46) while Billing read the
 * real `subscriptions` row and rendered "No active subscription". Signed out,
 * one screen claimed a paid subscription and the other correctly claimed none.
 *
 * Both screens now call `useStudioBilling()`, so a disagreement between them is
 * no longer expressible: there is one fetch, one state machine, and one set of
 * label constants below.
 *
 * The state machine deliberately separates the three cases the old code
 * collapsed into "no subscription":
 *
 *   - `signed-out`      → we cannot know this person's plan. Say so; do not
 *                         claim they have none, and never claim they have one.
 *   - `no-subscription` → signed in, the `subscriptions` table has no active
 *                         row. This is a fact we measured.
 *   - `error`           → the read failed. We do not know. Say that too, rather
 *                         than degrading into a confident "No active
 *                         subscription".
 */

import { useCallback, useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { getStudioUser } from '@/services/auth-provider'

/** A row of the `subscriptions` table. */
export interface StudioSubscription {
  id: string
  user_id: string
  plan_type: string
  billing_cycle: string
  status: string
  start_date: string
  end_date: string
  total_amount: number
  currency: string
  cancel_at_period_end?: boolean
}

/** A row of the `subscription_payments` table. */
export interface StudioPayment {
  id: string
  payment_date: string
  total_amount: number
  currency: string
  payment_status: string
  payment_provider: string
  ngenius_order_ref?: string
}

export type StudioBillingState =
  | 'loading'
  | 'signed-out'
  | 'no-subscription'
  | 'active'
  | 'error'

export interface StudioBillingSnapshot {
  state: StudioBillingState
  subscription: StudioSubscription | null
  /** `null` means "not known" (signed out, failed, or not requested). */
  payments: StudioPayment[] | null
  error: string | null
}

// Both screens render these exact strings, so neither can drift into its own
// phrasing of the same fact.
export const PLAN_LOADING_LABEL = 'Checking your plan…'
export const PLAN_SIGNED_OUT_LABEL = 'Sign in to see your plan'
export const PLAN_NONE_LABEL = 'No active subscription'
export const PLAN_ERROR_LABEL = "We couldn't load your plan"

const PLAN_NAMES: Record<string, string> = {
  free: 'Free',
  basic: 'Basic',
  business: 'Business',
  ai: 'AI Credits',
}

const CYCLE_NAMES: Record<string, string> = {
  month: 'Monthly',
  year: 'Yearly',
  'one-time': 'One-time',
  monthly: 'Monthly',
  yearly: 'Yearly',
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  AED: 'AED ',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
}

export function planName(planType: string): string {
  return PLAN_NAMES[planType] ?? planType
}

export function cycleName(cycle: string): string {
  return CYCLE_NAMES[cycle] ?? cycle
}

export function formatAmount(amount: number, currency: string): string {
  return `${CURRENCY_SYMBOLS[currency] ?? `${currency} `}${amount.toFixed(2)}`
}

/**
 * The one-line answer to "what plan am I on?" — identical on every screen that
 * asks. Only the `active` branch may name a plan, and the name comes from the
 * fetched row, never from a literal.
 */
/**
 * The plan STATE words translate; the plan NAMES (Free / Basic / Business /
 * AI Credits) do not — those are the product's tier names, the same in every
 * locale. `copy` defaults to the English constants above so both callers keep
 * rendering the identical string when they do not pass one.
 */
export function planHeadline(
  snapshot: StudioBillingSnapshot,
  copy?: {
    loading: string
    signedOut: string
    noSubscription: string
    errorLabel: string
  },
): string {
  const loading = copy?.loading ?? PLAN_LOADING_LABEL
  const signedOut = copy?.signedOut ?? PLAN_SIGNED_OUT_LABEL
  const none = copy?.noSubscription ?? PLAN_NONE_LABEL
  const failed = copy?.errorLabel ?? PLAN_ERROR_LABEL
  switch (snapshot.state) {
    case 'loading':
      return loading
    case 'signed-out':
      return signedOut
    case 'error':
      return failed
    case 'active':
      return snapshot.subscription
        ? planName(snapshot.subscription.plan_type)
        : none
    case 'no-subscription':
      return none
  }
}

/**
 * The supporting line under the headline — price and billing cycle read off the
 * subscription row, or `null` when there is no measured plan to describe.
 *
 * Returning `null` (rather than a placeholder like "0 of 10 outputs") is the
 * point: the workspace has no usage-metering source in this codebase, so the
 * honest amount of detail to show without one is none.
 */
export function planDetail(snapshot: StudioBillingSnapshot): string | null {
  const subscription = snapshot.subscription
  if (snapshot.state !== 'active' || !subscription) return null
  return `${formatAmount(subscription.total_amount, subscription.currency)} · ${cycleName(subscription.billing_cycle)}`
}

/**
 * Read the current person's plan (and optionally their payments).
 *
 * Resolves the user through `getStudioUser()` rather than `supabase.auth` so
 * the answer is the same under both identity providers (`supabase` and the
 * `cogo` DGX provider) — the previous Billing-only implementation called
 * `supabase.auth.getUser()` directly, which is wrong for the `cogo` provider.
 */
export async function fetchStudioBilling(
  options: { withPayments?: boolean } = {},
): Promise<StudioBillingSnapshot> {
  const empty: StudioBillingSnapshot = {
    state: 'signed-out',
    subscription: null,
    payments: null,
    error: null,
  }

  let userId: string
  try {
    const { data } = await getStudioUser()
    if (!data.user) return empty
    userId = data.user.id
  } catch {
    return empty
  }

  try {
    const subscriptionQuery = supabase
      .from('subscriptions')
      .select('*')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    const paymentsQuery = options.withPayments
      ? supabase
          .from('subscription_payments')
          .select('*')
          .eq('user_id', userId)
          .order('payment_date', { ascending: false })
      : null

    const [subscriptionResult, paymentResult] = await Promise.all([
      subscriptionQuery,
      paymentsQuery,
    ])

    if (subscriptionResult.error) throw subscriptionResult.error

    const subscription =
      (subscriptionResult.data as StudioSubscription | null) ?? null

    return {
      state: subscription ? 'active' : 'no-subscription',
      subscription,
      payments: options.withPayments
        ? ((paymentResult?.data as StudioPayment[] | null) ?? [])
        : null,
      error: null,
    }
  } catch (cause) {
    // Never degrade a failed read into a confident "No active subscription" —
    // that is the exact shape of the bug this module exists to prevent.
    return {
      state: 'error',
      subscription: null,
      payments: null,
      error: cause instanceof Error ? cause.message : 'Billing lookup failed',
    }
  }
}

const LOADING_SNAPSHOT: StudioBillingSnapshot = {
  state: 'loading',
  subscription: null,
  payments: null,
  error: null,
}

export interface UseStudioBillingResult extends StudioBillingSnapshot {
  /**
   * Apply a locally-known change to the fetched subscription (Billing's
   * "cancel at period end" writes the row, then reflects it here) without
   * inventing a subscription that was never fetched.
   */
  patchSubscription: (patch: Partial<StudioSubscription>) => void
}

export function useStudioBilling(
  options: { withPayments?: boolean } = {},
): UseStudioBillingResult {
  const withPayments = options.withPayments ?? false
  const [snapshot, setSnapshot] =
    useState<StudioBillingSnapshot>(LOADING_SNAPSHOT)

  useEffect(() => {
    let active = true
    setSnapshot(LOADING_SNAPSHOT)
    void fetchStudioBilling({ withPayments }).then((next) => {
      if (active) setSnapshot(next)
    })
    return () => {
      active = false
    }
  }, [withPayments])

  const patchSubscription = useCallback(
    (patch: Partial<StudioSubscription>) => {
      setSnapshot((current) =>
        current.subscription
          ? {
              ...current,
              subscription: { ...current.subscription, ...patch },
            }
          : current,
      )
    },
    [],
  )

  return { ...snapshot, patchSubscription }
}
